import {useCallback} from 'react';

import {distributorT} from '@/lib/types/distributor.types';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {
  BaseContextT,
  createAppContext,
  createAppHook,
  createAppProvider,
  createProviderConfig,
  useBaseContext,
} from '@/providers/components/baseProvider';

// Config
const {table, columns, statsView} = createProviderConfig({
  table: 'Distributor',
  statsView: 'distributor_stats',
});

type TableT = typeof table;
type ColumnsT = typeof columns;
type StatsViewT = typeof statsView;
type CustomContextT = {
  getDistributor: (id: number) => Promise<distributorT>;
};

// Context
type DistributorsContextT = BaseContextT<TableT, StatsViewT, ColumnsT, CustomContextT>;
const DistributorsContext = createAppContext<DistributorsContextT>(table);

// Provider
const ContextProvider = ({children}: {children: React.ReactNode}) => {
  const {isAdministrator} = useAuth();
  const baseContext = useBaseContext<TableT, ColumnsT, StatsViewT>();

  /*******************************************************************
   * Gets a distributor record from the database.
   * @param {number} id - The ID of the distributor to get.
   * @returns {Promise<retailerT | null>} - The distributor object.
   * @throws {Error} - If there's an error in getting the distributor.
   ********************************************************************/
  const getDistributor = useCallback(
    async (id: number) => {
      if (!isAdministrator) throw new Error('Error getting retailer');
      try {
        const {data, error} = await SupabaseInstance.supabase
          .from('Distributor')
          .select()
          .eq('distributor_id', id)
          .single();

        if (!data || error) throw new Error('Distributor not found');
        return data;
      } catch (error) {
        throw new Error((error as Error).message);
      }
    },
    [isAdministrator]
  );

  return (
    <DistributorsContext.Provider
      value={{
        ...baseContext,
        getDistributor,
      }}
    >
      {children}
    </DistributorsContext.Provider>
  );
};

// Add this to the AppProviders.tsx file
export const DistributorsProvider = createAppProvider({
  table,
  columns,
  statsView,
  ContextProvider,
});

// Hook
export const useDistributors = createAppHook<TableT, StatsViewT, ColumnsT, CustomContextT>(DistributorsContext);
